import { buildUnitReferenceIndexWithRemap } from './manualUnitIdentity'
import type { AnchoredRow, UnitMapping } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import { buildLedgerSafeWitnessUnitStream } from './ledgerSafeWitnessUnitStream'

export interface LedgerSafeUnassignedFragment {
  witnessId: string
  unitUuid: string
  unitLabel?: string
  text: string
  start: number
  end: number
  previousRowId: string | null
  nextRowId: string | null
}

export interface LedgerSafeBoundaryReconcileResult {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  unassignedFragments: LedgerSafeUnassignedFragment[]
  idRemap: Record<string, string[]>
}

export interface LedgerSafeOrderedUnit {
  unit: MicroUnit
  index: number
}

type OrderedUnit = LedgerSafeOrderedUnit

const spanOverlapLength = (leftStart: number, leftEnd: number, rightStart: number, rightEnd: number): number => {
  const start = Math.max(leftStart, rightStart)
  const end = Math.min(leftEnd, rightEnd)
  return Math.max(0, end - start)
}

const nowIso = (): string => new Date().toISOString()

const normalizeIdRemap = (idRemap: unknown): Record<string, string[]> => {
  if (!idRemap || typeof idRemap !== 'object') {
    return {}
  }

  const normalized: Record<string, string[]> = {}
  Object.entries(idRemap as Record<string, unknown>).forEach(([source, targets]) => {
    if (!Array.isArray(targets)) {
      return
    }
    const sourceKey = String(source || '').trim()
    if (!sourceKey) {
      return
    }
    const nextTargets = Array.from(new Set(
      targets
        .map(target => String(target || '').trim())
        .filter(Boolean)
    ))
    if (nextTargets.length > 0) {
      normalized[sourceKey] = nextTargets
    }
  })
  return normalized
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  return left.labelNumber - right.labelNumber
})

export const getLedgerSafeOrderedUnits = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
): OrderedUnit[] => buildLedgerSafeWitnessUnitStream(segmentationResult)
  .map((entry, index) => ({
    unit: entry.unit,
    index,
  }))

const buildReconcileIdRemap = ({
  previousUnits,
  nextUnits,
  existing
}: {
  previousUnits: OrderedUnit[]
  nextUnits: OrderedUnit[]
  existing: Record<string, string[]>
}): Record<string, string[]> => {
  const remap: Record<string, string[]> = { ...existing }
  const survivingNextUnitIds = new Set(
    nextUnits
      .map(entry => String(entry.unit.unit_uuid || entry.unit.unit_id || '').trim())
      .filter(Boolean)
  )

  previousUnits.forEach(previousEntry => {
    const previousUuid = String(previousEntry.unit.unit_uuid || previousEntry.unit.unit_id || '').trim()
    const previousStart = Number(previousEntry.unit.start_position)
    const previousEnd = Number(previousEntry.unit.end_position)
    if (!previousUuid || !Number.isFinite(previousStart) || !Number.isFinite(previousEnd)) {
      return
    }
    if (survivingNextUnitIds.has(previousUuid)) {
      return
    }

    let bestNextUuid = ''
    let bestOverlap = 0
    nextUnits.forEach(nextEntry => {
      const nextUuid = String(nextEntry.unit.unit_uuid || nextEntry.unit.unit_id || '').trim()
      const nextStart = Number(nextEntry.unit.start_position)
      const nextEnd = Number(nextEntry.unit.end_position)
      if (!nextUuid || !Number.isFinite(nextStart) || !Number.isFinite(nextEnd)) {
        return
      }
      const overlap = spanOverlapLength(previousStart, previousEnd, nextStart, nextEnd)
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestNextUuid = nextUuid
      }
    })

    if (!bestNextUuid || bestNextUuid === previousUuid) {
      return
    }
    const existingTargets = Array.isArray(remap[previousUuid]) ? remap[previousUuid] : []
    if (!existingTargets.includes(bestNextUuid)) {
      remap[previousUuid] = [...existingTargets, bestNextUuid]
    }
  })

  return remap
}

const getActiveMapping = (row: AnchoredRow, witnessId: string): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)

const getWitnessCellState = (row: AnchoredRow, witnessId: string): 'mapped' | 'gap' | 'excluded' => {
  const witnessMappings = row.unitMappings.filter(mapping => mapping.witnessId === witnessId)
  if (witnessMappings.some(mapping => !mapping.excluded)) {
    return 'mapped'
  }
  if (witnessMappings.some(mapping => mapping.excluded)) {
    return 'excluded'
  }
  return 'gap'
}

const normalizeMappingText = (value: string | null | undefined): string =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const replaceWitnessMappings = (
  row: AnchoredRow,
  witnessId: string,
  nextMappings: UnitMapping[]
): AnchoredRow => ({
  ...row,
  unitMappings: [
    ...row.unitMappings.filter(mapping => mapping.witnessId !== witnessId),
    ...nextMappings
  ],
  status: row.status === 'confirmed' ? 'needs-review' : row.status,
  updatedAt: nowIso()
})

const deriveUnassignedFragmentsFromState = ({
  witnessId,
  segmentationResult,
  rows
}: {
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  rows: AnchoredRow[]
}): LedgerSafeUnassignedFragment[] => {
  if (!segmentationResult) {
    return []
  }

  const sortedRows = sortRows(rows)
  const orderedUnits = getLedgerSafeOrderedUnits(segmentationResult)
  const unitLookup = buildUnitReferenceIndexWithRemap({
    units: orderedUnits.map(entry => entry.unit),
    idRemap: segmentationResult.id_remap
  })
  const claimedUnitIndexes = new Set<number>()
  const rowIdByUnitIndex = new Map<number, string>()
  const rowIndexById = new Map(sortedRows.map((row, index) => [row.id, index]))
  const unresolvedMappings: Array<{ rowId: string; mapping: UnitMapping }> = []

  sortedRows.forEach(row => {
    const mapping = getActiveMapping(row, witnessId)
    if (!mapping) {
      return
    }
    const unitIndex = unitLookup.get(mapping.unitUuid)
    if (typeof unitIndex !== 'number' || claimedUnitIndexes.has(unitIndex)) {
      unresolvedMappings.push({ rowId: row.id, mapping })
      return
    }
    claimedUnitIndexes.add(unitIndex)
    rowIdByUnitIndex.set(unitIndex, row.id)
  })

  unresolvedMappings.forEach(({ rowId, mapping }) => {
    const mappingText = normalizeMappingText(mapping.textSnapshot)
    if (!mappingText) {
      return
    }
    const matchingIndexes = orderedUnits
      .filter(entry => !claimedUnitIndexes.has(entry.index))
      .filter(entry => normalizeMappingText(entry.unit.unit_text_content) === mappingText)
      .map(entry => entry.index)
    if (matchingIndexes.length !== 1) {
      return
    }
    const matchedIndex = matchingIndexes[0]
    if (typeof matchedIndex !== 'number') {
      return
    }
    claimedUnitIndexes.add(matchedIndex)
    rowIdByUnitIndex.set(matchedIndex, rowId)
  })

  const claimedIndexesAscending = (): number[] =>
    Array.from(rowIdByUnitIndex.keys()).sort((left, right) => left - right)

  const isForwardRowBoundary = (previousRowId: string | null, nextRowId: string | null): boolean => {
    if (!previousRowId || !nextRowId) {
      return true
    }
    const previousIndex = rowIndexById.get(previousRowId)
    const nextIndex = rowIndexById.get(nextRowId)
    if (typeof previousIndex !== 'number' || typeof nextIndex !== 'number') {
      return false
    }
    return previousIndex < nextIndex
  }

  const findFillableRowAfter = ({
    afterRowId,
    beforeRowId
  }: {
    afterRowId: string | null
    beforeRowId: string | null
  }): string | null => {
    if (!afterRowId) {
      return null
    }
    const afterIndex = rowIndexById.get(afterRowId)
    if (typeof afterIndex !== 'number') {
      return null
    }
    const beforeIndex = beforeRowId ? rowIndexById.get(beforeRowId) : undefined
    const maxIndex = typeof beforeIndex === 'number' ? beforeIndex : sortedRows.length
    for (let index = afterIndex + 1; index < maxIndex; index += 1) {
      const candidate = sortedRows[index]
      if (getWitnessCellState(candidate, witnessId) !== 'mapped') {
        return candidate.id
      }
    }
    return null
  }

  const findFillableRowBefore = ({
    beforeRowId,
    afterRowId
  }: {
    beforeRowId: string | null
    afterRowId: string | null
  }): string | null => {
    if (!beforeRowId) {
      return null
    }
    const beforeIndex = rowIndexById.get(beforeRowId)
    if (typeof beforeIndex !== 'number') {
      return null
    }
    const afterIndex = afterRowId ? rowIndexById.get(afterRowId) : undefined
    const minIndex = typeof afterIndex === 'number' ? afterIndex : -1
    for (let index = beforeIndex - 1; index > minIndex; index -= 1) {
      const candidate = sortedRows[index]
      if (getWitnessCellState(candidate, witnessId) !== 'mapped') {
        return candidate.id
      }
    }
    return null
  }

  return orderedUnits
    .filter(entry => !claimedUnitIndexes.has(entry.index))
    .map(entry => {
      const claimedIndexes = claimedIndexesAscending()
      const previousClaimedIndex = claimedIndexes
        .filter(index => index < entry.index)
        .sort((left, right) => right - left)[0]
      const nextClaimedIndex = claimedIndexes
        .filter(index => index > entry.index)
        .sort((left, right) => left - right)[0]

      const anchoredPreviousRowId = typeof previousClaimedIndex === 'number'
        ? (rowIdByUnitIndex.get(previousClaimedIndex) || null)
        : null
      const anchoredNextRowId = typeof nextClaimedIndex === 'number'
        ? (rowIdByUnitIndex.get(nextClaimedIndex) || null)
        : null

      const fillableNextRowId = findFillableRowAfter({
        afterRowId: anchoredPreviousRowId,
        beforeRowId: anchoredNextRowId
      })
      const fillablePreviousRowId = findFillableRowBefore({
        beforeRowId: anchoredNextRowId,
        afterRowId: anchoredPreviousRowId
      })
      const previousRowId = anchoredPreviousRowId || fillablePreviousRowId
      const nextRowId = fillableNextRowId || anchoredNextRowId
      const hasForwardBoundary = isForwardRowBoundary(previousRowId, nextRowId)

      return {
        witnessId,
        unitUuid: String(entry.unit.unit_uuid || entry.unit.unit_id || ''),
        unitLabel: entry.unit.unit_label,
        text: String(entry.unit.unit_text_content || ''),
        start: Number(entry.unit.start_position || 0),
        end: Number(entry.unit.end_position || 0),
        previousRowId: hasForwardBoundary ? previousRowId : null,
        nextRowId: hasForwardBoundary ? nextRowId : null
      }
    })
}

export const deriveLedgerSafeUnassignedFragments = ({
  witnessId,
  segmentationResult,
  rows
}: {
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  rows: AnchoredRow[]
}): LedgerSafeUnassignedFragment[] => deriveUnassignedFragmentsFromState({
  witnessId,
  segmentationResult,
  rows
})

export const reconcileLedgerSafeBoundaryEdit = ({
  witnessId,
  previousSegmentationResult,
  nextSegmentationResult,
  rows
}: {
  witnessId: string
  previousSegmentationResult: CompleteMicroSegmentationResult | null | undefined
  nextSegmentationResult: CompleteMicroSegmentationResult | null | undefined
  rows: AnchoredRow[]
}): LedgerSafeBoundaryReconcileResult => {
  const sortedRows = sortRows(rows)
  if (!nextSegmentationResult) {
    throw new Error('Next segmentation result is required')
  }

  const orderedNextUnits = getLedgerSafeOrderedUnits(nextSegmentationResult)
  const orderedPreviousUnits = getLedgerSafeOrderedUnits(previousSegmentationResult)
  const nextLookup = buildUnitReferenceIndexWithRemap({
    units: orderedNextUnits.map(entry => entry.unit),
    idRemap: nextSegmentationResult.id_remap
  })
  const previousLookup = buildUnitReferenceIndexWithRemap({
    units: orderedPreviousUnits.map(entry => entry.unit),
    idRemap: previousSegmentationResult?.id_remap
  })
  const normalizedIdRemap = buildReconcileIdRemap({
    previousUnits: orderedPreviousUnits,
    nextUnits: orderedNextUnits,
    existing: normalizeIdRemap(nextSegmentationResult.id_remap)
  })

  const claimedNextIndexes = new Set<number>()
  const rowIdByNextIndex = new Map<number, string>()
  const changedRowIds: string[] = []

  const updatedRows = sortedRows.map(row => {
    const currentMapping = getActiveMapping(row, witnessId)
    if (!currentMapping) {
      return row
    }

    const nextIndex = nextLookup.get(currentMapping.unitUuid)
    if (typeof nextIndex !== 'number') {
      changedRowIds.push(row.id)
      return replaceWitnessMappings(row, witnessId, [])
    }
    const nextEntry = orderedNextUnits[nextIndex]
    if (!nextEntry || claimedNextIndexes.has(nextIndex)) {
      changedRowIds.push(row.id)
      return replaceWitnessMappings(row, witnessId, [])
    }

    claimedNextIndexes.add(nextIndex)
    rowIdByNextIndex.set(nextIndex, row.id)

    const nextUnitUuid = String(nextEntry.unit.unit_uuid || nextEntry.unit.unit_id || '')
    const nextUnitLabel = nextEntry.unit.unit_label || currentMapping.unitLabel
    const nextText = String(nextEntry.unit.unit_text_content || currentMapping.textSnapshot || '')
    const previousIndex = previousLookup.get(currentMapping.unitUuid)
    const previousEntry = typeof previousIndex === 'number'
      ? orderedPreviousUnits[previousIndex]
      : undefined
    const previousText = String(
      previousEntry?.unit.unit_text_content || currentMapping.textSnapshot || ''
    )
    const normalizedNextText = normalizeMappingText(nextText)
    const underlyingTextChanged = normalizeMappingText(previousText) !== normalizedNextText
    const cachedTextNeedsUpdate = normalizeMappingText(currentMapping.textSnapshot) !== normalizedNextText
    // Unit labels are display caches and can renumber globally after a local edit.
    // Text snapshots are caches too: only refresh one when the underlying unit
    // changed across this edit. Pre-existing whitespace drift must not turn a
    // local segmentation operation into a chapter-wide durable rewrite.
    const mappingChanged =
      nextUnitUuid !== currentMapping.unitUuid ||
      (underlyingTextChanged && cachedTextNeedsUpdate)

    if (!mappingChanged) {
      return row
    }

    changedRowIds.push(row.id)
    return replaceWitnessMappings(row, witnessId, [{
      ...currentMapping,
      unitUuid: nextUnitUuid,
      unitLabel: nextUnitLabel,
      textSnapshot: nextText,
      addedAt: currentMapping.addedAt || nowIso()
    }])
  })

  const unassignedFragments = deriveUnassignedFragmentsFromState({
    witnessId,
    segmentationResult: nextSegmentationResult,
    rows: updatedRows
  })

  const previousUnitIds = new Set(orderedPreviousUnits.map(entry => String(entry.unit.unit_uuid || entry.unit.unit_id || '')))
  const finalIdRemap = Object.keys(normalizedIdRemap).reduce<Record<string, string[]>>((accumulator, source) => {
    if (!previousUnitIds.has(source)) {
      return accumulator
    }
    accumulator[source] = normalizedIdRemap[source]
    return accumulator
  }, {})

  return {
    updatedRows,
    changedRowIds,
    unassignedFragments,
    idRemap: finalIdRemap
  }
}
